import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'motion/react'
import { cn } from '../../lib/utils'

/**
 * chanhdai.com's elastic slider, rewritten for this project.
 *
 * Two changes from the registry item. Its styles are Tailwind v4 (`bg-(--var)`,
 * `--spacing()`, `text-sm/none`, bare colour tokens) and we are on v3 with HSL
 * triplets, so the same look is spelled with arbitrary values and `hsl(var(…))`
 * — the same treatment `wheel-picker.tsx` and `animated-toggle-group.tsx` got.
 * And it is controlled only: every slider in this app is driven by form state,
 * so the copy of Radix's `useControllableState` it shipped with is dead weight.
 *
 * The label and the value live inside the track, and the handle fades out of
 * the way when it would sit on top of either.
 */

// Drag detection & rubber band
const CLICK_THRESHOLD = 3
const DEAD_ZONE = 32
const MAX_CURSOR_RANGE = 200
const MAX_STRETCH = 8

// Layout offsets used by the "handle dodges label/value" calculation.
const HANDLE_BUFFER = 8
const LABEL_OFFSET = 12 + 4
const VALUE_OFFSET = 12 - 8

function clamp(value: number, low: number, high: number) {
  return Math.max(low, Math.min(high, value))
}

function decimalsForStep(step: number): number {
  const text = step.toString()
  const dot = text.indexOf('.')
  return dot === -1 ? 0 : text.length - dot - 1
}

function roundValue(value: number, step: number): number {
  const raw = Math.round(value / step) * step
  return Number.parseFloat(raw.toFixed(decimalsForStep(step)))
}

/** Magnetic snap to the nearest decile when within 3.125% of it. */
function snapToDecile(rawValue: number, min: number, max: number): number {
  const normalized = (rawValue - min) / (max - min)
  const nearest = Math.round(normalized * 10) / 10
  if (Math.abs(normalized - nearest) <= 0.03125) {
    return min + nearest * (max - min)
  }
  return rawValue
}

export interface ElasticSliderProps {
  /** Shown inside the track, on the left. */
  label: string
  value: number
  onValueChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  /** How the value reads inside the track. Defaults to the number itself. */
  formatValue?: (value: number) => string
  className?: string
  'aria-label'?: string
}

export function ElasticSlider({
  label,
  value,
  onValueChange,
  min = 0,
  max = 1,
  step = 0.01,
  formatValue,
  className,
  'aria-label': ariaLabel,
}: ElasticSliderProps) {
  const shouldReduceMotion = useReducedMotion()

  const wrapperRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const valueRef = useRef<HTMLSpanElement>(null)

  const [isInteracting, setIsInteracting] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  /** Ring only for Tab focus or keyboard nudges, not for a pointer press. */
  const [keyboardFocusRing, setKeyboardFocusRing] = useState(false)

  // Pointer session state — mutable, does not trigger re-renders.
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null)
  const pendingPointerFocusRef = useRef(false)
  const isClickRef = useRef(true)
  const animRef = useRef<ReturnType<typeof animate> | null>(null)
  const wrapperRectRef = useRef<DOMRect | null>(null)
  const scaleRef = useRef(1)

  const percentage = ((value - min) / (max - min)) * 100
  const isActive = isInteracting || isHovered
  const displayValue = formatValue ? formatValue(value) : value.toFixed(decimalsForStep(step))

  // Fill + handle driven by a single motion value for imperative updates.
  const fillPercent = useMotionValue(percentage)
  const fillWidth = useTransform(fillPercent, (pct) => `${pct}%`)
  const handleLeft = useTransform(fillPercent, (pct) => `max(4px, calc(${pct}% - 8px))`)

  // Rubber band: widens the track and pulls it left when dragged past bounds.
  const rubberStretch = useMotionValue(0)
  const rubberWidth = useTransform(rubberStretch, (s) => `calc(100% + ${Math.abs(s)}px)`)
  const rubberX = useTransform(rubberStretch, (s) => (s < 0 ? s : 0))

  // Sync from props when not interacting and no spring is in flight.
  useEffect(() => {
    if (!isInteracting && !animRef.current) {
      fillPercent.jump(percentage)
    }
  }, [percentage, isInteracting, fillPercent])

  const positionToValue = useCallback(
    (clientX: number) => {
      const rect = wrapperRectRef.current
      if (!rect) return min

      const sceneX = (clientX - rect.left) / scaleRef.current
      const nativeWidth = wrapperRef.current?.offsetWidth ?? rect.width
      if (!nativeWidth) return min
      const percent = clamp(sceneX / nativeWidth, 0, 1)

      return clamp(min + percent * (max - min), min, max)
    },
    [min, max]
  )

  const percentFromValue = useCallback(
    (candidate: number) => ((candidate - min) / (max - min)) * 100,
    [min, max]
  )

  // Animate the fill to a target percent, or jump when the viewer prefers
  // reduced motion — the position still updates, only the spring is skipped.
  const animateFillTo = useCallback(
    (targetPercent: number) => {
      animRef.current?.stop()

      if (shouldReduceMotion) {
        fillPercent.jump(targetPercent)
        animRef.current = null
        return
      }

      animRef.current = animate(fillPercent, targetPercent, {
        type: 'spring',
        stiffness: 300,
        damping: 25,
        mass: 0.8,
        onComplete: () => {
          animRef.current = null
        },
      })
    },
    [fillPercent, shouldReduceMotion]
  )

  const computeRubberStretch = useCallback((clientX: number, sign: number) => {
    const rect = wrapperRectRef.current
    if (!rect) return 0

    const distancePast = sign < 0 ? rect.left - clientX : clientX - rect.right
    const overflow = Math.max(0, distancePast - DEAD_ZONE)

    return sign * MAX_STRETCH * Math.sqrt(Math.min(overflow / MAX_CURSOR_RANGE, 1))
  }, [])

  const handlePointerDown = useCallback((event: React.PointerEvent) => {
    event.preventDefault()
    ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)

    pointerDownPos.current = { x: event.clientX, y: event.clientY }
    isClickRef.current = true
    setIsInteracting(true)

    pendingPointerFocusRef.current = true
    setKeyboardFocusRing(false)

    // Pointer interaction moves focus onto the slider so the keyboard picks up
    // where the finger left off, without drawing the focus ring.
    trackRef.current?.focus({ preventScroll: true })
    requestAnimationFrame(() => {
      pendingPointerFocusRef.current = false
    })

    // Snapshot the wrapper rect so later maths is immune to layout shifts.
    const wrapper = wrapperRef.current
    if (wrapper) {
      const rect = wrapper.getBoundingClientRect()
      wrapperRectRef.current = rect
      scaleRef.current = wrapper.offsetWidth ? rect.width / wrapper.offsetWidth : 1
    }
  }, [])

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isInteracting || !pointerDownPos.current) return

      const dx = event.clientX - pointerDownPos.current.x
      const dy = event.clientY - pointerDownPos.current.y

      if (isClickRef.current && Math.hypot(dx, dy) > CLICK_THRESHOLD) {
        isClickRef.current = false
        setIsDragging(true)
      }

      if (isClickRef.current) return

      const rect = wrapperRectRef.current
      if (rect && !shouldReduceMotion) {
        if (event.clientX < rect.left) {
          rubberStretch.jump(computeRubberStretch(event.clientX, -1))
        } else if (event.clientX > rect.right) {
          rubberStretch.jump(computeRubberStretch(event.clientX, 1))
        } else {
          rubberStretch.jump(0)
        }
      }

      const next = positionToValue(event.clientX)
      animRef.current?.stop()
      animRef.current = null
      fillPercent.jump(percentFromValue(next))
      onValueChange(roundValue(next, step))
    },
    [
      isInteracting,
      positionToValue,
      percentFromValue,
      onValueChange,
      step,
      fillPercent,
      rubberStretch,
      computeRubberStretch,
      shouldReduceMotion,
    ]
  )

  const handlePointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (!isInteracting) return

      if (isClickRef.current) {
        // A coarse slider (ten positions or fewer) snaps to the nearest step;
        // a continuous one keeps the decile-magnetic behaviour.
        const rawValue = positionToValue(event.clientX)
        const discreteSteps = (max - min) / step
        const snapped =
          discreteSteps <= 10
            ? clamp(min + Math.round((rawValue - min) / step) * step, min, max)
            : snapToDecile(rawValue, min, max)

        animateFillTo(percentFromValue(snapped))
        onValueChange(roundValue(snapped, step))
      }

      if (!shouldReduceMotion && rubberStretch.get() !== 0) {
        animate(rubberStretch, 0, { type: 'spring', visualDuration: 0.35, bounce: 0.15 })
      }

      setIsInteracting(false)
      setIsDragging(false)
      pointerDownPos.current = null
    },
    [
      isInteracting,
      positionToValue,
      percentFromValue,
      onValueChange,
      min,
      max,
      step,
      animateFillTo,
      rubberStretch,
      shouldReduceMotion,
    ]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Shift + arrow is a fast nudge of ten steps.
      const arrowStep = event.shiftKey ? step * 10 : step
      let next: number | null = null

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          next = value + arrowStep
          break
        case 'ArrowLeft':
        case 'ArrowDown':
          next = value - arrowStep
          break
        case 'Home':
          next = min
          break
        case 'End':
          next = max
          break
        default:
          return
      }

      event.preventDefault()
      setKeyboardFocusRing(true)

      const snapped = roundValue(clamp(next, min, max), step)
      animateFillTo(percentFromValue(snapped))
      onValueChange(snapped)
    },
    [value, min, max, step, animateFillTo, percentFromValue, onValueChange]
  )

  const handleTrackFocus = useCallback(() => {
    if (!pendingPointerFocusRef.current) setKeyboardFocusRing(true)
  }, [])

  const handleTrackBlur = useCallback(() => setKeyboardFocusRing(false), [])

  // Measure the label and the value so the handle knows where it would overlap
  // either of them, and fades there.
  const [dodge, setDodge] = useState({ left: 38, right: 72 })

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const measure = () => {
      const trackWidth = wrapper.offsetWidth
      if (trackWidth <= 0) return

      const labelEl = labelRef.current
      const valueEl = valueRef.current

      const left = labelEl
        ? ((LABEL_OFFSET + labelEl.offsetWidth + HANDLE_BUFFER) / trackWidth) * 100
        : 38
      const right = valueEl
        ? ((trackWidth - VALUE_OFFSET - valueEl.offsetWidth - HANDLE_BUFFER) / trackWidth) * 100
        : 72

      setDodge((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(wrapper)
    if (labelRef.current) observer.observe(labelRef.current)
    if (valueRef.current) observer.observe(valueRef.current)

    return () => observer.disconnect()
  }, [label, displayValue])

  const valueDodge = percentage < dodge.left || percentage > dodge.right
  const handleOpacity = !isActive ? 0 : valueDodge ? 0.1 : isDragging ? 0.8 : 0.5

  const discreteSteps = (max - min) / step
  const hashMarkCount = discreteSteps <= 10 ? discreteSteps - 1 : 9
  const hashMarkPct = (index: number) =>
    discreteSteps <= 10 ? (((index + 1) * step) / (max - min)) * 100 : (index + 1) * 10

  return (
    <div
      ref={wrapperRef}
      data-slot="elastic-slider"
      className={cn(
        '[--elastic-slider-height:2.25rem] [--elastic-slider-radius:var(--radius)]',
        '[--elastic-slider-bg:hsl(var(--muted))]',
        '[--elastic-slider-fill:hsl(var(--muted-foreground)/0.1)]',
        '[--elastic-slider-fill-active:hsl(var(--muted-foreground)/0.2)]',
        '[--elastic-slider-hash:hsl(var(--muted-foreground)/0.3)]',
        '[--elastic-slider-handle:hsl(var(--foreground))]',
        '[--elastic-slider-label:hsl(var(--muted-foreground))]',
        '[--elastic-slider-focus:hsl(var(--foreground))]',
        'relative h-[var(--elastic-slider-height)]',
        className
      )}
    >
      <motion.div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        data-slot="elastic-slider-track"
        data-active={isActive}
        data-focus-visible={keyboardFocusRing}
        aria-label={ariaLabel ?? label}
        aria-orientation="horizontal"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={displayValue}
        className={cn(
          'group/elastic-slider absolute inset-0 cursor-pointer touch-none select-none overflow-hidden outline-none',
          'rounded-[var(--elastic-slider-radius)] bg-[var(--elastic-slider-bg)]',
          'data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-[hsl(var(--ring)/0.5)] data-[focus-visible=true]:ring-offset-1 data-[focus-visible=true]:ring-offset-background'
        )}
        style={{ width: rubberWidth, x: rubberX }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onFocus={handleTrackFocus}
        onBlur={handleTrackBlur}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          data-slot="elastic-slider-hash-marks"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
        >
          {Array.from({ length: Math.max(0, hashMarkCount) }, (_, index) => (
            <div
              key={index}
              className={cn(
                'absolute top-1/2 h-2 w-px -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-200',
                'bg-transparent group-data-[active=true]/elastic-slider:bg-[var(--elastic-slider-hash)]'
              )}
              style={{ left: `${hashMarkPct(index)}%` }}
            />
          ))}
        </div>

        <motion.div
          data-slot="elastic-slider-fill"
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 transition-colors',
            'bg-[var(--elastic-slider-fill)] group-data-[active=true]/elastic-slider:bg-[var(--elastic-slider-fill-active)]'
          )}
          style={{ width: fillWidth }}
        />

        <motion.div
          data-slot="elastic-slider-handle"
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 h-5 w-1 rounded-full bg-[var(--elastic-slider-handle)]"
          style={{ left: handleLeft, y: '-50%' }}
          animate={{
            opacity: handleOpacity,
            scaleX: isActive ? 1 : 0.25,
            scaleY: isActive && valueDodge ? 0.75 : 1,
          }}
          transition={
            shouldReduceMotion
              ? { duration: 0 }
              : {
                  scaleX: { type: 'spring', visualDuration: 0.25, bounce: 0.15 },
                  scaleY: { type: 'spring', visualDuration: 0.2, bounce: 0.1 },
                  opacity: { duration: 0.15 },
                }
          }
        />

        <span
          ref={labelRef}
          data-slot="elastic-slider-label"
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 inline-flex -translate-y-1/2 items-center text-sm font-medium leading-none text-[var(--elastic-slider-label)] transition-colors"
        >
          {label}
        </span>

        <span
          ref={valueRef}
          data-slot="elastic-slider-value"
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-sm font-medium leading-none transition-colors',
            'text-[var(--elastic-slider-label)] group-data-[active=true]/elastic-slider:text-[var(--elastic-slider-focus)]'
          )}
        >
          {displayValue}
        </span>
      </motion.div>
    </div>
  )
}
